// Typed event bus. UI, audio, analytics and hints subscribe here instead of
// being called directly from gameplay code (v1's game3d.js did the latter).

export interface EventMap {
  'run:started': { districtId: string };
  'run:ended': { score: number; distance: number };
  'score:awarded': { points: number; total: number; label?: string };
  'coin:collected': { total: number };
  'combo:changed': { combo: number; multiplier: number };
  'player:jumped': { double: boolean };
  'player:slid': Record<string, never>;
  'player:swept': { enemyId: string };
  'player:landed': Record<string, never>;
  'player:lane-changed': { lane: number };
  'player:roof-ride': Record<string, never>;
  'player:near-miss': Record<string, never>;
  'player:hit': { fatal: boolean };
  'speed:changed': { gameSpeed: number };
  'powerup:activated': { typeId: 'star' | 'multiplier' | 'magnet' };
  'powerup:deactivated': Record<string, never>;
  'powerup:tick': { remaining: number };
  'belt:promoted': { name: string; color: number };
  'enemy:jumped': { enemyId: string };
  'combat:started': { enemyId: string };
  'combat:ended': { victory: boolean };
  'construction:cleared': Record<string, never>;
}

type Handler<K extends keyof EventMap> = (payload: EventMap[K]) => void;

export class EventBus {
  private readonly handlers = new Map<keyof EventMap, Set<Handler<never>>>();

  on<K extends keyof EventMap>(event: K, handler: Handler<K>): () => void {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(handler);
    return () => set.delete(handler);
  }

  emit<K extends keyof EventMap>(event: K, payload: EventMap[K]): void {
    const set = this.handlers.get(event);
    if (!set) return;
    for (const handler of set) (handler as Handler<K>)(payload);
  }

  clear(): void {
    this.handlers.clear();
  }
}
